// src/components/assignments/TransferItemModal.tsx

import React, { useState, useEffect } from 'react';
import { assignmentAPI } from '../../services/assignmentAPI';
import { userAPI } from '../../services/userAPI';
import { User } from '../../types/user';
import { Assignment, ASSIGNMENT_REASONS, AssignmentReason } from '../../types/assignment';

interface TransferItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assignment: Assignment | null;
}

const TransferItemModal: React.FC<TransferItemModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  assignment
}) => {
  const [targetOfficerId, setTargetOfficerId] = useState<string>('');
  const [transferReason, setTransferReason] = useState<AssignmentReason>('Transfer');
  const [notes, setNotes] = useState<string>('');
  const [confirmTransfer, setConfirmTransfer] = useState<boolean>(false);
  
  const [officers, setOfficers] = useState<User[]>([]);
  const [targetOfficer, setTargetOfficer] = useState<User | null>(null);
  const [currentOfficer, setCurrentOfficer] = useState<User | null>(null);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && assignment) {
      loadModalData();
    }
  }, [isOpen, assignment]);

  // Find target officer details
  useEffect(() => {
    if (targetOfficerId && officers.length > 0) {
      const officer = officers.find(o => o.userId === targetOfficerId);
      setTargetOfficer(officer || null);
    } else {
      setTargetOfficer(null);
    }
  }, [targetOfficerId, officers]);

  const loadModalData = async (): Promise<void> => {
    if (!assignment) return;

    setLoading(true);
    setError(null);

    try {
      // Load all officers
      const officersResponse = await userAPI.getUsers();
      const activeOfficers = officersResponse.users.filter((user: User) => 
        user.employmentStatus === 'Active' && user.userId !== assignment.assignedToUserId
      );
      setOfficers(activeOfficers);

      // Load current officer details
      try {
        const currentOfficerResponse = await userAPI.getUser(assignment.assignedToUserId);
        setCurrentOfficer(currentOfficerResponse);
      } catch (err) {
        console.warn('Could not load current officer details:', err);
        setCurrentOfficer({
          userId: assignment.assignedToUserId,
          firstName: assignment.assignedToUserName?.split(' ')[0] || 'Unknown',
          lastName: assignment.assignedToUserName?.split(' ').slice(1).join(' ') || 'Officer',
          badgeNumber: assignment.assignedToUserBadge || 'N/A',
          employmentStatus: 'Active'
        } as User);
      }

    } catch (err) {
      console.error('Error loading transfer data:', err);
      setError('Failed to load transfer data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): string | null => {
    if (!assignment) {
      return 'No assignment selected';
    }

    if (assignment.status !== 'Active') {
      return 'Can only transfer active assignments';
    }

    if (!targetOfficerId) {
      return 'Please select a target officer';
    }

    if (!targetOfficer) {
      return 'Target officer not found';
    }

    if (targetOfficer.employmentStatus !== 'Active') {
      return 'Cannot transfer to inactive officer';
    }

    if (targetOfficerId === assignment.assignedToUserId) {
      return 'Cannot transfer to the same officer';
    }

    if (!confirmTransfer) {
      return 'Please confirm the transfer';
    }

    // Check for equipment restrictions
    if (targetOfficer.equipmentRestrictions && 
        targetOfficer.equipmentRestrictions.toLowerCase().includes('no equipment')) {
      return 'Target officer has equipment restrictions';
    }

    // Check firearms certification for firearm transfers
    if (assignment.itemName.toLowerCase().includes('firearm') || 
        assignment.itemName.toLowerCase().includes('gun') ||
        assignment.itemName.toLowerCase().includes('pistol') ||
        assignment.itemName.toLowerCase().includes('rifle')) {
      
      const hasFirearmCert = targetOfficer.certificationLevels?.some(cert =>
        cert.toLowerCase().includes('firearm') || 
        cert.toLowerCase().includes('weapons')
      );

      if (!hasFirearmCert) {
        return 'Target officer must have firearms certification for firearm transfers';
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!assignment) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Return the current assignment
      const returnResponse = await assignmentAPI.returnAssignment(assignment.assignmentId, {
        condition: 'Good',
        notes: `Transferred to ${targetOfficer?.firstName} ${targetOfficer?.lastName} (${targetOfficer?.badgeNumber})`
      });

      if (!returnResponse.success) {
        throw new Error('Failed to return current assignment');
      }

      // Step 2: Create new assignment to target officer
      const createResponse = await assignmentAPI.createAssignment({
        assignment: {
          inventoryItemId: assignment.inventoryItemId,
          assignedToUserId: targetOfficerId,
          assignmentReason: transferReason,
          notes: `Transferred from ${currentOfficer?.firstName} ${currentOfficer?.lastName} (${currentOfficer?.badgeNumber}). ${notes.trim()}`
        }
      });

      if (!createResponse.success) {
        throw new Error('Failed to create new assignment');
      }

      onSuccess();
      handleClose();

    } catch (err) {
      console.error('Error processing transfer:', err);
      setError(err instanceof Error ? err.message : 'Failed to transfer equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    // Reset form state
    setTargetOfficerId('');
    setTransferReason('Transfer');
    setNotes('');
    setConfirmTransfer(false);
    setError(null);
    setTargetOfficer(null);
    setCurrentOfficer(null);
    onClose();
  };

  if (!isOpen || !assignment) return null;

  const isFirearm = assignment.itemName.toLowerCase().includes('firearm') || 
                   assignment.itemName.toLowerCase().includes('gun') ||
                   assignment.itemName.toLowerCase().includes('pistol') ||
                   assignment.itemName.toLowerCase().includes('rifle');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Transfer Equipment</h2>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 text-2xl disabled:opacity-50"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading transfer details...</p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <span className="text-red-400 text-xl mr-3">⚠️</span>
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          {/* Form Content */}
          {!loading && (
            <>
              {/* Equipment Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Equipment Being Transferred</h3>
                <div className="space-y-1 text-sm">
                  <div><strong>Item:</strong> {assignment.itemName}</div>
                  {assignment.itemSerialNumber && (
                    <div><strong>Serial Number:</strong> {assignment.itemSerialNumber}</div>
                  )}
                  <div><strong>Assignment Date:</strong> {new Date(assignment.assignmentDate).toLocaleDateString()}</div>
                  <div><strong>Assignment Reason:</strong> {assignment.assignmentReason}</div>
                  {isFirearm && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                      <span className="text-red-800 text-xs font-medium">
                        🔫 FIREARM - Special handling required
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Officer */}
              {currentOfficer && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-2">Currently Assigned To</h3>
                  <div className="space-y-1 text-sm text-blue-800">
                    <div><strong>Officer:</strong> {currentOfficer.firstName} {currentOfficer.lastName}</div>
                    <div><strong>Badge:</strong> {currentOfficer.badgeNumber}</div>
                    {currentOfficer.rank && (
                      <div><strong>Rank:</strong> {currentOfficer.rank}</div>
                    )}
                    {currentOfficer.assignment && (
                      <div><strong>Assignment:</strong> {currentOfficer.assignment}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Target Officer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer To Officer <span className="text-red-500">*</span>
                </label>
                <select
                  value={targetOfficerId}
                  onChange={(e) => setTargetOfficerId(e.target.value)}
                  required
                  className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  disabled={submitting}
                >
                  <option value="">Select Target Officer</option>
                  {officers.map(officer => (
                    <option key={officer.userId} value={officer.userId}>
                      {officer.firstName} {officer.lastName} ({officer.badgeNumber}) - {officer.rank}
                    </option>
                  ))}
                </select>
              </div>

              {/* Target Officer Details */}
              {targetOfficer && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Target Officer Details</h4>
                  <div className="space-y-1 text-sm text-green-800">
                    <div><strong>Name:</strong> {targetOfficer.firstName} {targetOfficer.lastName}</div>
                    <div><strong>Badge:</strong> {targetOfficer.badgeNumber}</div>
                    <div><strong>Rank:</strong> {targetOfficer.rank}</div>
                    <div><strong>Assignment:</strong> {targetOfficer.assignment}</div>
                    {targetOfficer.certificationLevels && targetOfficer.certificationLevels.length > 0 && (
                      <div><strong>Certifications:</strong> {targetOfficer.certificationLevels.join(', ')}</div>
                    )}
                    
                    {/* Firearm certification check */}
                    {isFirearm && (
                      <div className="mt-2 p-2 border rounded">
                        {targetOfficer.certificationLevels?.some(cert =>
                          cert.toLowerCase().includes('firearm') || 
                          cert.toLowerCase().includes('weapons')
                        ) ? (
                          <div className="text-green-800">
                            ✅ <strong>Firearm Certified:</strong> Officer has required firearms certification
                          </div>
                        ) : (
                          <div className="text-red-800">
                            ❌ <strong>Missing Certification:</strong> Officer may not have firearms certification
                          </div>
                        )}
                      </div>
                    )}

                    {targetOfficer.equipmentRestrictions && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <strong className="text-yellow-800">Restrictions:</strong>
                        <span className="text-yellow-700 ml-1">{targetOfficer.equipmentRestrictions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transfer Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value as AssignmentReason)}
                  required
                  className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  disabled={submitting}
                >
                  <option value="Transfer">Transfer</option>
                  <option value="Promotion">Promotion</option>
                  <option value="Replacement-Lost">Replacement - Lost</option>
                  <option value="Replacement-Damaged">Replacement - Damaged</option>
                  <option value="Temporary">Temporary Assignment</option>
                  {Object.values(ASSIGNMENT_REASONS).map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {/* Transfer Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Date
                </label>
                <input
                  type="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  disabled={submitting}
                />
              </div>

              {/* Transfer Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Optional notes about this transfer..."
                  disabled={submitting}
                />
              </div>

              {/* Transfer Process Explanation */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Transfer Process</h4>
                <div className="text-sm text-yellow-800 space-y-1">
                  <div>1. Current assignment will be marked as "Returned"</div>
                  <div>2. New assignment will be created for target officer</div>
                  <div>3. Assignment history will be preserved for audit trail</div>
                  <div>4. Inventory item will be updated to reflect new assignment</div>
                </div>
              </div>

              {/* Confirmation Checkbox */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="confirmTransfer"
                    checked={confirmTransfer}
                    onChange={(e) => setConfirmTransfer(e.target.checked)}
                    required
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={submitting}
                  />
                  <label htmlFor="confirmTransfer" className="text-sm text-gray-700">
                    <strong>I confirm this transfer:</strong> I verify that both officers are authorized 
                    for this equipment transfer, all department policies have been followed, and proper 
                    documentation will be maintained.
                    {isFirearm && (
                      <span className="block mt-1 text-red-700">
                        <strong>Firearm Transfer:</strong> I additionally confirm that the target officer 
                        has current firearms certification and is authorized to carry department firearms.
                      </span>
                    )}
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          {!loading && (
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !confirmTransfer}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing Transfer...</span>
                  </div>
                ) : (
                  'Transfer Equipment'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TransferItemModal;